import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import FuzzySet from "fuzzyset";

import { useGetVersionConfigQuery } from "fond/api";
import { InferredIcon } from "fond/map/Legend";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { selectLayersFromConfig } from "fond/utils/configurations";
import { Modal } from "fond/widgets";

interface CarveConfigModalProps {
  open: boolean;
  versionId: string;
  onClose: () => void;
  onConfirm: (layerKey: string, attributeName: string) => void;
}

const BOUNDARY_LAYER_KEY = "PN Boundaries";

/**
 * Fuzzy find a layer by key with a specified similarity threshold.
 * This is used to handle cases where the expected layer key might not be an exact match in the configuration,
 * allowing for some flexibility in naming while still guiding users towards the correct selection for BOM carving.
 */
const fuzzyFindMatch = (items: LayerConfig[], searchTerm: string, threshold: number = 0.6): LayerConfig | null => {
  const keys = items.map((item) => item.Key);
  const fuzzySet = FuzzySet(keys);
  const matches = fuzzySet.get(searchTerm);
  if (!matches || matches.length === 0) return null;

  const [bestScore, bestKey] = matches.reduce(([maxScore, maxKey], [score, key]) => (score > maxScore ? [score, key] : [maxScore, maxKey]));

  if (bestScore >= threshold) {
    return items.find((item) => item.Key === bestKey) ?? null;
  }
  return null;
};

const CarveConfigModal: React.FC<CarveConfigModalProps> = ({ open, versionId, onClose, onConfirm }) => {
  const [selectedLayerKey, setSelectedLayerKey] = useState<string>("");
  const [selectedAttribute, setSelectedAttribute] = useState<string>("");

  const { data: versionConfig } = useGetVersionConfigQuery(versionId, { skip: !versionId });

  const layerOptions = useMemo((): LayerConfig[] => {
    if (!versionConfig) return [];
    return selectLayersFromConfig(versionConfig, ["LAYER"]) as LayerConfig[];
  }, [versionConfig]);

  const selectedLayer = useMemo(() => {
    return layerOptions.find((layer) => layer.Key === selectedLayerKey) ?? null;
  }, [layerOptions, selectedLayerKey]);

  const attributeOptions = useMemo(() => {
    return selectedLayer?.Attributes ?? [];
  }, [selectedLayer]);

  // Only set default options on initial load, not on every re-render when dependencies change
  const defaultLayerSet = useRef(false);
  const defaultAttributeSet = useRef(false);

  // Default options. If available, pre-select the "PN Boundaries" layer or anything like it and "PN" attribute as
  // these are most commonly used for BOM carving in CTS.
  useEffect(() => {
    if (!defaultLayerSet.current && layerOptions.length > 0) {
      defaultLayerSet.current = true;
      const defaultLayer = fuzzyFindMatch(layerOptions, BOUNDARY_LAYER_KEY);
      if (defaultLayer) setSelectedLayerKey(defaultLayer.Key);
    }
  }, [layerOptions]);

  useEffect(() => {
    if (!defaultAttributeSet.current && attributeOptions.length > 0) {
      defaultAttributeSet.current = true;
      const defaultAttr = attributeOptions.find((attr) => attr.Name === "PN");
      if (defaultAttr) setSelectedAttribute(defaultAttr.Name);
    }
  }, [attributeOptions]);

  const handleLayerChange = (_: React.SyntheticEvent, value: LayerConfig | null) => {
    setSelectedLayerKey(value?.Key ?? "");
    setSelectedAttribute("");
  };

  const handleAttributeChange = (_: React.SyntheticEvent, value: string | null) => {
    setSelectedAttribute(value ?? "");
  };

  const handleConfirm = () => {
    if (selectedLayerKey && selectedAttribute) {
      onConfirm(selectedLayerKey, selectedAttribute);
    }
  };

  const handleClose = () => {
    setSelectedLayerKey("");
    setSelectedAttribute("");
    onClose();
  };

  return (
    <Modal
      open={open}
      header="Carve BOM by cabinet"
      onClose={handleClose}
      content={
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a boundary layer and attribute to carve the BOM
          </Typography>

          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
            Boundary layer
          </Typography>
          <Autocomplete
            options={layerOptions}
            value={selectedLayer}
            onChange={handleLayerChange}
            getOptionLabel={(option) => option.Label}
            isOptionEqualToValue={(option, value) => option.Key === value.Key}
            renderOption={({ key, ...props }, option) => (
              <li key={`${key}-${option.ID}`} {...props}>
                {versionConfig && (
                  <Box height="22px" mr={1}>
                    <InferredIcon entity={option} config={versionConfig} />
                  </Box>
                )}
                <Typography>{option.Label}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment:
                    selectedLayer && versionConfig ? (
                      <Box height="22px" mr={1}>
                        <InferredIcon entity={selectedLayer} config={versionConfig} />
                      </Box>
                    ) : null,
                }}
                placeholder="Select a layer..."
                variant="outlined"
              />
            )}
            size="small"
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
            ID attribute
          </Typography>
          <Autocomplete
            options={attributeOptions.map((attr) => attr.Name)}
            value={selectedAttribute || null}
            onChange={handleAttributeChange}
            disabled={!selectedLayerKey}
            renderInput={(params) => <TextField {...params} placeholder="Select an attribute..." variant="outlined" />}
            size="small"
          />
        </>
      }
      actions={
        <>
          <Button color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleConfirm} disabled={!selectedLayerKey || !selectedAttribute}>
            Confirm
          </Button>
        </>
      }
    />
  );
};

export default CarveConfigModal;
